import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from "recharts";

interface DayStep {
  day: string;
  steps: number;
}

interface DailyStepsChartProps {
  data?: DayStep[];
  goal?: number;
  bare?: boolean;
}

const TEAL = "#20BCBB";
const RED = "#FF2D55";

const formatK = (v: number) => {
  if (v >= 1000) {
    const val = v / 1000;
    return `${val.toFixed(val % 1 === 0 ? 0 : 1)}k`;
  }
  return `${v}`;
};

function getYMax(data: DayStep[], goal: number) {
  const maxValue = Math.max(...data.map((d) => d.steps), goal);
  const step = 2000;
  return Math.ceil(maxValue / step) * step;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.payload?.steps ?? 0;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{String(label).slice(0, 2)}</p>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block bg-[#20BCBB]" />
        <span className="text-[#1F1F1F] font-medium">Steps · {value.toLocaleString()}</span>
      </div>
    </div>
  );
};

/**
 * Renders a single stacked bar: teal below goal, red above.
 * Also draws both labels (`base` inside teal, `+over` inside red) in white.
 */
const StepBarShape = (props: any) => {
  const { x, y, width, height, payload, goal, yMax } = props;
  if (width <= 0 || height <= 0) return null;

  const steps: number = payload?.steps ?? 0;
  const r = 4;

  // Bar top is at y, bottom is at y + height. Total value spans [0, steps].
  // Scale: 1 unit = height / steps (only if steps > 0)
  const overValue = Math.max(0, steps - goal);
  const baseValue = Math.min(steps, goal);

  // Height in px per unit for THIS bar's own value
  // But since Recharts sizes `height` to `steps` (single dataKey), we map by ratio
  const overHeight = steps > 0 ? (overValue / steps) * height : 0;
  const baseHeight = height - overHeight;

  const baseY = y + overHeight;
  const overY = y;

  const baseInside = baseHeight >= 18;
  const overInside = overHeight >= 12;
  const baseOutside = baseValue > 0 && !baseInside;
  const overOutside = overValue > 0 && !overInside;

  return (
    <g>
      {/* Teal (base) portion */}
      {baseHeight > 0 && (
        <rect
          x={x}
          y={baseY}
          width={width}
          height={baseHeight}
          fill={TEAL}
          rx={overValue > 0 ? 0 : r}
          ry={overValue > 0 ? 0 : r}
        />
      )}

      {/* Red (over-goal) portion */}
      {overHeight > 0 && (
        <rect
          x={x}
          y={overY}
          width={width}
          height={overHeight}
          fill={RED}
          rx={r}
          ry={r}
        />
      )}

      {/* +excess label — inside when tall enough, else above the bar in red */}
      {overValue > 0 && (
        overInside ? (
          <text
            x={x + width / 2}
            y={overY + Math.min(12, overHeight / 2 + 3)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#FFFFFF"
            fontSize={11}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {`+${formatK(overValue)}`}
          </text>
        ) : (
          <text
            x={x + width / 2}
            y={(baseOutside ? y - 20 : y - 6)}
            textAnchor="middle"
            dominantBaseline="auto"
            fill={RED}
            fontSize={11}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {`+${formatK(overValue)}`}
          </text>
        )
      )}

      {/* Base value label — inside when tall enough, else above the bar in teal */}
      {baseValue > 0 && (
        baseInside ? (
          <text
            x={x + width / 2}
            y={baseY + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#FFFFFF"
            fontSize={11}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {formatK(baseValue)}
          </text>
        ) : (
          <text
            x={x + width / 2}
            y={y - 6}
            textAnchor="middle"
            dominantBaseline="auto"
            fill={TEAL}
            fontSize={11}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {formatK(baseValue)}
          </text>
        )
      )}
    </g>
  );
};

const DailyStepsChart: React.FC<DailyStepsChartProps> = ({
  data = [],
  goal = 10000,
  bare = false,
}) => {
  const yMax = getYMax(data, goal);
  const total = data.reduce((sum, d) => sum + d.steps, 0);

  const chart = (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart
        data={data}
        barCategoryGap="20%"
        margin={{ top: 12, right: 8, left: 8, bottom: 0 }}
      >
        <Tooltip content={<CustomTooltip />} cursor={false} />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          dy={6}
          tickFormatter={(v) => String(v).slice(0, 2)}
        />
        <YAxis hide domain={[0, yMax]} />
        <ReferenceLine y={goal} stroke="#B7B7B7" strokeDasharray="4 4" strokeWidth={1} />
        <Bar
          dataKey="steps"
          maxBarSize={44}
          minPointSize={2}
          isAnimationActive={true}
          shape={(shapeProps: any) => (
            <StepBarShape {...shapeProps} goal={goal} yMax={yMax} />
          )}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (bare) return chart;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Daily Steps
        </span>
        <div className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter">
          Total : <span className="font-semibold text-[#1F1F1F]">{formatK(total)}</span>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 relative">{chart}</div>
    </div>
  );
};

export default DailyStepsChart;
